import React, { useMemo } from "react";
import { mockTestimonials } from "../mock";

export const IndustrySection = () => {
  // Get all testimonials sorted by order
  const recommendations = mockTestimonials.sort((a, b) => a.order - b.order);

  // Calculate grid layout based on exact text character count
  const testimonialsWithLayout = useMemo(() => {
    // Calculate text lengths
    const withLengths = recommendations.map((rec) => ({
      ...rec,
      textLength: rec.text.length,
    }));

    // Sort by text length (descending) to assign sizes from longest to shortest
    const sortedByLength = [...withLengths].sort(
      (a, b) => b.textLength - a.textLength
    );

    // Assign sizes based on character count with precise thresholds
    const sizeMap = new Map();

    sortedByLength.forEach((rec, index) => {
      const length = rec.textLength;

      // Very long testimonials (>500 chars) get large cards (2x2)
      if (length > 500) {
        sizeMap.set(rec.id, "large");
      }
      // Long testimonials (350-500 chars) get tall cards (1x2) for vertical space
      else if (length > 350) {
        sizeMap.set(rec.id, "tall");
      }
      // Medium-long testimonials (200-350 chars) get wide cards (2x1) for horizontal space
      else if (length > 200) {
        sizeMap.set(rec.id, "wide");
      }
      // Medium testimonials (100-200 chars) get tall cards (1x2)
      else if (length > 100) {
        sizeMap.set(rec.id, "tall");
      }
      // Short testimonials (<100 chars) get normal cards (1x1)
      else {
        sizeMap.set(rec.id, "normal");
      }
    });

    // Return original order with assigned sizes
    return withLengths.map((rec) => ({
      ...rec,
      size: sizeMap.get(rec.id) || "normal",
    }));
  }, [recommendations]);

  return (
    <section id="testimonials" className="py-20 px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            What Industry Professionals Say
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Hear from those who've experienced the transformation firsthand
          </p>
        </div>

        {/* Bento Grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6"
          style={{
            gridAutoRows: "min-content",
            gridAutoFlow: "row dense",
          }}
        >
          {testimonialsWithLayout.map((rec) => {
            const sizeClasses = {
              normal: "md:col-span-1 md:row-span-1",
              wide: "md:col-span-2 md:row-span-1",
              tall: "md:col-span-1 md:row-span-2",
              large: "md:col-span-2 md:row-span-2",
            };

            return (
              <div
                key={rec.id}
                className={`bg-white rounded-2xl p-6 lg:p-8 border border-gray-200 hover:border-purple-300 hover:shadow-xl transition-all duration-300 flex flex-col group ${
                  sizeClasses[rec.size]
                }`}
              >
                {/* Testimonial Content */}
                <div className="flex-1 mb-6">
                  <p className="text-gray-700 leading-relaxed text-base lg:text-lg italic">
                    "{rec.text}"
                  </p>
                </div>

                {/* Author Info */}
                <div className="flex items-center gap-4 pt-4 border-t border-gray-100 mt-auto">
                  {rec.image && (
                    <img
                      src={rec.image}
                      alt={rec.name}
                      className="w-12 h-12 lg:w-14 lg:h-14 rounded-full object-cover border-2 border-purple-200 flex-shrink-0 group-hover:border-purple-400 transition-colors"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-gray-900 text-sm lg:text-base xl:text-lg truncate">
                      {rec.name}
                    </h4>
                    <p className="text-xs lg:text-sm text-purple-600 font-medium truncate">
                      {rec.profession}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Trust Bar */}
        <div className="mt-16">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* Students Trained */}
            <div className="relative rounded-2xl p-8 lg:p-10 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
              {/* Background Image */}
              <div className="absolute inset-0 z-0">
                <img
                  src="/images/Manoj_Bajpayee+Barry_John.jpeg"
                  alt="Students Training"
                  className="w-full h-full object-cover"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/90 via-purple-700/85 to-purple-800/90"></div>
              </div>
              {/* Content */}
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30">
                    <svg
                      className="w-8 h-8"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-5xl lg:text-6xl font-bold mb-2 drop-shadow-lg">
                  3000+
                </div>
                <div className="text-lg lg:text-xl font-semibold text-purple-100 drop-shadow-md">
                  Students Trained
                </div>
                <p className="text-sm text-purple-200 mt-2 opacity-90 drop-shadow-sm">
                  Transforming aspiring actors into professionals
                </p>
              </div>
            </div>

            {/* Years Experience */}
            <div className="relative rounded-2xl p-8 lg:p-10 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
              {/* Background Image */}
              <div className="absolute inset-0 z-0">
                <img
                  src="/images/bjlp-hero.jpg"
                  alt="Barry John Experience"
                  className="w-full h-full object-cover"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/90 via-purple-600/85 to-indigo-700/90"></div>
              </div>
              {/* Content */}
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30">
                    <svg
                      className="w-8 h-8"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-5xl lg:text-6xl font-bold mb-2 drop-shadow-lg">
                  50+
                </div>
                <div className="text-lg lg:text-xl font-semibold text-indigo-100 drop-shadow-md">
                  Years Experience
                </div>
                <p className="text-sm text-indigo-200 mt-2 opacity-90 drop-shadow-sm">
                  Decades of excellence in acting education
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
